import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconFileDollar } from '@tabler/icons-react';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import {
  PRICE_METHOD_CONTRACT,
  createPriceContract,
  getCustomerPricing,
  listPriceContracts,
  setPriceContractInForce,
  type PriceContractDto,
} from './api';

interface Props {
  customerId: string;
}

const CONTRACTS_KEY = 'customer-price-contracts';

/**
 * A customer's negotiated prices — M5.15.
 *
 * **Only one contract prices anything.** A customer can hold several — last year's, this year's,
 * a draft — and exactly the one marked in force is consulted, and only while it is active and
 * unexpired. Showing all of them with one marked is the honest picture; showing only the live one
 * would hide the renewal somebody is about to switch to.
 *
 * **In force is a separate act from saving.** It is the click that changes what the next order
 * costs, so it is not buried in a form somebody opened to fix a typo in a description.
 */
export function CustomerContractsCard({ customerId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canWrite = has('pricing:write');

  const [creating, setCreating] = useState(false);
  const [number, setNumber] = useState('');
  const [description, setDescription] = useState('');

  const contracts = useQuery({
    queryKey: [CONTRACTS_KEY, customerId] as const,
    queryFn: ({ signal }) => listPriceContracts(customerId, signal),
  });

  // Read here rather than passed in: the two cards are independent, and the customer form has
  // no business knowing about pricing methods to wire them together.
  const pricing = useQuery({
    queryKey: ['customer-pricing', customerId] as const,
    queryFn: ({ signal }) => getCustomerPricing(customerId, signal),
  });

  const isContractMethod = pricing.data?.priceMethod === PRICE_METHOD_CONTRACT;

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY, customerId] });
  };

  const create = useMutation({
    mutationFn: () => createPriceContract(customerId, {
      contractNumber: number.trim(),
      description: description.trim() || null,
    }),
    onSuccess: async () => {
      setCreating(false);
      setNumber('');
      setDescription('');
      await invalidate();
    },
  });

  const setInForce = useMutation({
    mutationFn: (priceContractId: string | null) =>
      setPriceContractInForce(customerId, priceContractId),
    onSuccess: invalidate,
  });

  const columns: DataColumn<PriceContractDto>[] = [
    {
      key: 'contractNumber',
      label: t('pricing.contractNumber'),
      render: (c) => <Text fw={600}>{c.contractNumber}</Text>,
      card: 'title',
    },
    {
      key: 'description',
      label: t('pricing.contractDescription'),
      render: (c) => c.description ?? '—',
    },
    {
      key: 'items',
      label: t('pricing.contractItems'),
      align: 'right',
      render: (c) => c.itemCount,
    },
    {
      key: 'expires',
      label: t('pricing.contractExpires'),
      // An empty expiry is not a gap in the data — it means the contract does not expire, which
      // is a thing legacy's own test could not express.
      render: (c) => (c.expiresAtUtc
        ? new Date(c.expiresAtUtc).toLocaleDateString()
        : t('pricing.contractNoExpiry')),
    },
    {
      key: 'inForce',
      label: t('pricing.contractStatus'),
      render: (c) => (c.inForce
        ? <Badge color="green" variant="light">{t('pricing.contractInForceYes')}</Badge>
        : <Badge color="gray" variant="light">{t('pricing.contractInForceNo')}</Badge>),
      card: 'aside',
    },
  ];

  return (
    <Card withBorder padding="md">
      <Stack>
        <Group justify="space-between">
          <Group gap="xs">
            <IconFileDollar size={18} />
            <Text fw={600}>{t('pricing.contractsTitle')}</Text>
          </Group>
          {canWrite ? (
            <Button size="compact-sm" variant="light" onClick={() => setCreating(true)}>
              {t('pricing.contractNew')}
            </Button>
          ) : null}
        </Group>

        {/* Said plainly rather than left to be discovered on an invoice: a contract on a customer
            priced by method 1 or 2 is simply not consulted. */}
        {pricing.isSuccess && !isContractMethod ? (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            {t('pricing.contractsNotUsed')}
          </Alert>
        ) : null}

        {contracts.isLoading ? (
          <Loader size="sm" />
        ) : contracts.isError ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {contracts.error instanceof ApiError
              ? contracts.error.problem.title
              : t('pricing.contractsLoadFailed')}
          </Alert>
        ) : contracts.data && contracts.data.length > 0 ? (
          <ResponsiveTable
            rows={contracts.data}
            columns={columns}
            rowKey={(c) => c.priceContractId}
            minWidth={620}
            actions={(c) => (canWrite ? (
              <Button
                size="compact-sm"
                variant="subtle"
                loading={setInForce.isPending}
                onClick={() => setInForce.mutate(c.inForce ? null : c.priceContractId)}
              >
                {c.inForce ? t('pricing.contractStandDown') : t('pricing.contractUse')}
              </Button>
            ) : null)}
          />
        ) : (
          <Text c="dimmed" size="sm">{t('pricing.contractsEmpty')}</Text>
        )}

        {setInForce.isError ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {setInForce.error instanceof ApiError
              ? setInForce.error.problem.title
              : t('pricing.contractInForceFailed')}
          </Alert>
        ) : null}
      </Stack>

      <Modal opened={creating} onClose={() => setCreating(false)} title={t('pricing.contractNew')}>
        <Stack>
          <TextInput
            label={t('pricing.contractNumber')}
            value={number}
            onChange={(e) => setNumber(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t('pricing.contractDescription')}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />

          {create.isError ? (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {create.error instanceof ApiError
                ? create.error.problem.title
                : t('pricing.contractCreateFailed')}
            </Alert>
          ) : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreating(false)}>{t('shell.cancel')}</Button>
            <Button
              loading={create.isPending}
              disabled={number.trim() === ''}
              onClick={() => create.mutate()}
            >
              {t('pricing.contractCreate')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
